import * as React from 'react';
import * as SplashScreen from 'expo-splash-screen';
import { useFonts } from 'expo-font';

import { ApplicationState, useAppStore } from '../store/index';
import { AnalyticsLogsData } from '../store/slices/analytics';
const Brands = require('../../../brands.json');
import { InitErrorCode } from '../types/InitError';
import { KalibraFont } from '../constants/KalibraTheme';
import { Platform } from 'react-native';
import { getValue } from 'src/api/storage';
import { useAnalytics } from './useAnalytics';
import { useHandshake } from 'src/core/hooks/useHandshake';
import { useSetFirstRun } from './useGlobalVar';
import { useNetInfo } from '@react-native-community/netinfo';

const initializeResources = async (
  setIsFirstLoad: (firstRun: boolean) => void,
  preAuth: () => Promise<object>,
  addAnalyticsLog: (log: AnalyticsLogsData) => void,
  setBrands: (brands: any) => void
): Promise<InitErrorCode> => {
  try {
    const brandsResponse = await preAuth();
    setBrands({ ...brandsResponse, ...Brands });
  } catch (error) {
    addAnalyticsLog({ function: 'initializeResources', data: error, logType: 'error' });
    return InitErrorCode.PRE_AUTH;
  }

  try {
    const isFirstLoad = await getValue('isFirstLoad');
    setIsFirstLoad(isFirstLoad == '' || isFirstLoad == 'true');
  } catch (error) {
    addAnalyticsLog({ function: 'initializeResources', data: error, logType: 'error' });
  }

  return InitErrorCode.OK;
};

export function useAppInitializer() {
  const [isInitComplete, setIsInitComplete] = React.useState<boolean>(false);
  const [initErrorCode, setInitErrorCode] = React.useState<InitErrorCode>(InitErrorCode.OK);
  const setAppInit = useAppStore((state: ApplicationState) => state.setAppPreInit);
  const isAppInit = useAppStore((state: ApplicationState) => state.isAppPreInit);
  const isVersionChecked = useAppStore((state: ApplicationState) => state.isVersionChecked);
  const setViewPortWidth = useAppStore((state: ApplicationState) => state.setViewPortWidth);
  const { preAuth } = useHandshake();
  const { addAnalyticsLog } = useAnalytics('useAppInitializer.ts');
  const setBrands = useAppStore((state) => state.setBrands);
  const setFirstLoad = useSetFirstRun();

  const [fontsLoaded] = useFonts(KalibraFont);

  React.useEffect(() => {
    setViewPortWidth();

    const windowResizer = () => {
      setViewPortWidth();
    };

    if (Platform.OS === 'web') {
      window.addEventListener('resize', windowResizer);
      return () => window.removeEventListener('resize', windowResizer);
    }
  }, [setViewPortWidth]);

  const netInfo = useNetInfo();
  const isOnline = netInfo.isConnected === true;

  React.useEffect(() => {
    if (isAppInit || !isVersionChecked || !fontsLoaded) {
      return;
    }

    const init = async () => {
      try {
        await SplashScreen.preventAutoHideAsync();
      } catch (error) {
        console.error(error);
      }

      if (isOnline == true) {
        const code = await initializeResources(setFirstLoad, preAuth, addAnalyticsLog, setBrands);
        setInitErrorCode(code);
        if (code == InitErrorCode.OK) {
          setAppInit();
          setIsInitComplete(true);
        }
      } else {
        setInitErrorCode(InitErrorCode.NO_NETWORK_CONNECTION);
      }

      try {
        await SplashScreen.hideAsync();
      } catch (error) {
        console.error('Error hiding splash screen:', error);
      }
    };

    init();
  }, [
    setAppInit,
    isAppInit,
    setFirstLoad,
    preAuth,
    addAnalyticsLog,
    isVersionChecked,
    setBrands,
    fontsLoaded,
    isOnline
  ]);

  return {
    initErrorCode,
    isInitComplete
  };
}
